// The Tempo panel's swing picker (Guitar Pro's "triplet feel") as two rows of
// pills: how the pair is split (Straight, the Kit's own swing, Triplet, Dotted,
// Scottish) and which notes swing (8ths, 16ths). The choices themselves and
// their band fields live in music/swingFeel.ts.

import type { StyleBand } from '@/music/style';
import {
  DEFAULT_SWING_FEEL,
  swingChoiceOf,
  swingRatioLabel,
  type SwingChoice,
} from '@/music/swingFeel';

export type SwingRatio = 'straight' | 'kit' | 'triplet' | 'dotted' | 'scottish';
export type SwingLevel = '8th' | '16th';

export const SWING_RATIO_CHOICES: readonly { id: SwingRatio; label: string }[] = [
  { id: 'straight', label: 'Straight' },
  { id: 'kit', label: 'Kit' },
  { id: 'triplet', label: 'Triplet' },
  { id: 'dotted', label: 'Dotted' },
  { id: 'scottish', label: 'Scottish' },
];

export const SWING_LEVEL_CHOICES: readonly { id: SwingLevel; label: string }[] = [
  { id: '8th', label: '8ths' },
  { id: '16th', label: '16ths' },
];

export function swingRatioOf(choice: SwingChoice): SwingRatio {
  return choice === 'straight' || choice === 'kit' ? choice : (choice.split('-')[0] as SwingRatio);
}

/** The notes that swing; none while straight, eighths with the kit. */
export function swingLevelOf(choice: SwingChoice): SwingLevel | null {
  return choice === 'straight'
    ? null
    : choice === 'kit' || !choice.endsWith('16th')
      ? '8th'
      : '16th';
}

/** The choice a ratio and a level make; a missing half takes the default feel's. */
export function composeSwing(ratio: SwingRatio | null, level: SwingLevel | null): SwingChoice {
  if (ratio === 'straight') return 'straight';
  if (ratio === 'kit') return 'kit';
  const fallback = DEFAULT_SWING_FEEL as Exclude<SwingChoice, 'straight'>;
  return `${ratio ?? swingRatioOf(fallback)}-${level ?? swingLevelOf(fallback)}` as SwingChoice;
}

/** The choice the picker lights: none while the bands being edited disagree. */
export function shownSwingChoice(
  band: Pick<StyleBand, 'bassSwing' | 'swingFeel' | 'swingWithKit'>,
  varies: ReadonlySet<keyof StyleBand>,
): SwingChoice | null {
  return varies.has('bassSwing') || varies.has('swingFeel') || varies.has('swingWithKit')
    ? null
    : swingChoiceOf(band);
}

/**
 * What the Feel section says about the kit while the band swings with it: the
 * ratio the loop's skip note measured, or that the band swings the triplet
 * because there is no kit or no clear skip note. Null for any other choice.
 */
export function kitSwingStatus(
  choice: SwingChoice | null,
  kit: { swing: number | null | undefined } | null,
): string | null {
  if (choice !== 'kit') return null;
  if (!kit) return 'No kit · triplet';
  if (kit.swing === undefined) return 'Kit not measured yet · triplet';
  if (kit.swing === null) return 'Kit has no clear swing · triplet';
  return `Kit swings ${swingRatioLabel(kit.swing)}`;
}
